/**
 * Super-admin user impersonation ("view as user") — client side.
 *
 * Mirrors Fund My Squad: start() asks the backend to mint a short-lived
 * impersonation token, stashes it (+ a display label) in sessionStorage, and
 * reloads so the whole app re-fetches as the target member. The token is attached
 * to every request by config/api.ts (`x-impersonate-token`). stop() clears it.
 *
 * sessionStorage (not localStorage) so the session dies with the tab and never
 * outlives the browser window.
 */
import { apiClient, IMPERSONATE_TOKEN_KEY } from '../config/api';
import { setActiveOrgId } from '../config/tenant';

const USER_KEY = 'crest_impersonate_user';

export interface ImpersonatedUser {
  id: string;
  name: string;
  email: string | null;
  organizationId: string;
}

export const impersonationService = {
  isActive(): boolean {
    try {
      return !!sessionStorage.getItem(IMPERSONATE_TOKEN_KEY);
    } catch {
      return false;
    }
  },

  getUser(): ImpersonatedUser | null {
    try {
      const raw = sessionStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as ImpersonatedUser) : null;
    } catch {
      return null;
    }
  },

  /** Begin viewing the app as `memberId`. Reloads on success. */
  async start(memberId: string): Promise<void> {
    const res = await apiClient.post(`/api/admin/impersonate/${encodeURIComponent(memberId)}`, {});
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Could not start impersonation.');
    }
    const data = await res.json();
    const user: ImpersonatedUser = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email ?? null,
      organizationId: data.user.organizationId,
    };
    sessionStorage.setItem(IMPERSONATE_TOKEN_KEY, data.token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    // Align the client tenant context with the target's org so branding/config match.
    if (user.organizationId) setActiveOrgId(user.organizationId);
    window.location.assign('/');
  },

  /** End the impersonation session and return to the members admin. */
  stop(): void {
    try {
      sessionStorage.removeItem(IMPERSONATE_TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    window.location.assign('/admin/members');
  },
};
